import { useCallback, useEffect, useLayoutEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import LibrasIcon from "../../assets/icons/libras-icon.svg";
import { getAnimationByTokens } from "../../utils/animationHandle";
import type { AnimationData } from "../../assets/glb/animations";
import { flushSync } from "react-dom";

import type { TourPlacement, TourStep } from "./steps";
import {
  CloseButton,
  ContentRow,
  FooterRow,
  IconInline,
  NavButton,
  NavButtons,
  Overlay,
  ProgressBar,
  ProgressContainer,
  ProgressFill,
  ProgressHeader,
  Spotlight,
  SpotlightOutline,
  TextContent,
  TooltipBox,
} from "./styles";
import { LibrasButtonMenu } from "../LibrasButtonMenu";

interface TourGuideProps {
  steps: TourStep[];
  isOpen: boolean;
  onClose: () => void;
  onFinish?: () => void;
  setOpenAi: (bol: boolean) => void;
  isMobile: boolean;
  setExpanded: (bol: boolean) => void;

  setAnimation: React.Dispatch<React.SetStateAction<AnimationData[]>>;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 20;


function resolvePlacement(
  rect: TargetRect | null,
  placement: TourPlacement = "bottom",
  isMobile = false
): TourPlacement {

  return placement;
}

function getTooltipStyle(
  rect: TargetRect | null,
  placement: TourPlacement,
  isMobile: boolean,
  offsetX = 0,
  offsetY = 0
): CSSProperties {
  if (isMobile) {
    switch (placement) {
      case "left":
        return {
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          transform: "none",
        };
      case "top":
        return {
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          transform: "none",
        };
      default:
        return {
          top: "auto",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          transform: "none",
        };
    }
  }

  if (placement === "center" || !rect) {
    return { top: "80%", left: "50%", width: "35%", transform: "translate(-50%, -50%)" };
  }

  switch (placement) {
    case "top":
      return {
        top: rect.top - TOOLTIP_GAP + offsetY,
        left: rect.left + rect.width / 2 + offsetX,
        transform: "translate(-50%, -100%)",
      };
    case "left":
      return {
        top: rect.top + rect.height / 2 + offsetY,
        left: rect.left - TOOLTIP_GAP + offsetX,
        transform: "translate(-100%, -50%)",
      };

    case "right":
      return {
        top: rect.top + rect.height / 2 + offsetY,
        left: rect.left + rect.width + TOOLTIP_GAP + offsetX,
        transform: "translate(0, -50%)"
      };

    case "right-top":
      return {
        top: rect.top + rect.height / 2 + offsetY,
        left: rect.left + rect.width + TOOLTIP_GAP + offsetX,
        transform: "translate(0, -50%)"
      };

    case "bottom":
    default:
      return {
        top: rect.top + rect.height + TOOLTIP_GAP + offsetY,
        left: rect.left + rect.width / 2 + offsetX,
        transform: "translate(-50%, 0)",
      };
  }
}


export function TourGuide({ steps, isOpen, onClose, onFinish, setAnimation, setOpenAi, isMobile, setExpanded }: TourGuideProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [loadingLibrasKey, setLoadingLibrasKey] = useState(false);
  

  const currentStep = steps[stepIndex];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === steps.length - 1;

  const progress =
    steps.length > 1
      ? (stepIndex / (steps.length - 1)) * 100
      : 0;


  const playLibrasWithLoading = async (step: TourStep) => {
    try {
      setLoadingLibrasKey(true);

      const handleStarted = () => {
        setLoadingLibrasKey(false);
        window.removeEventListener("LISA_ANIMATION_STARTED", handleStarted);
      };

      window.addEventListener("LISA_ANIMATION_STARTED", handleStarted, {
        once: true,
      });

      window.dispatchEvent(new CustomEvent("LISA_INTERRUPT"));

      const animations = await getAnimationByTokens(step.glosa);

      setAnimation?.(animations);

      window.dispatchEvent(new CustomEvent("LISA_RESUME"));
    } catch {
      setLoadingLibrasKey(false);
    } finally {
      setLoadingLibrasKey(false);
    }
  };

  const goNext = useCallback(() => {

    if (isLastStep) {
      onFinish?.();
      onClose();
      setOpenAi(false)
      return;
    }
    if (steps[stepIndex + 1]?.id === "control-chat-expand") {
      flushSync(() => {
        setOpenAi(true);
      });
    }

    if (steps[stepIndex + 1]?.id === "expand-chat") {
      flushSync(() => {
        setExpanded(true);
      });
    }

    setStepIndex((index) => index + 1);
  }, [stepIndex,
    steps,
    isLastStep,
    onFinish,
    onClose,
    setOpenAi,]);


  const goBack = useCallback(() => {

    if (steps[stepIndex - 1]?.id === "start-chat") {
      flushSync(() => {
        setOpenAi(false);
      });
    }


    if (isFirstStep) return;
    setStepIndex((index) => index - 1);
  }, [stepIndex,
    steps,
    isFirstStep,
    setOpenAi,]);



  const measureTarget = useCallback(() => {
    if (!currentStep) return;


    const el = document.querySelector(`[data-tutorial="${currentStep.target}"]`);
    if (!el) {
      setTargetRect({ top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0 });
      return;
    }

    const rect = el.getBoundingClientRect();
    setTargetRect({
      top: rect.top - SPOTLIGHT_PADDING,
      left: rect.left - SPOTLIGHT_PADDING,
      width: rect.width + SPOTLIGHT_PADDING * 2,
      height: rect.height + SPOTLIGHT_PADDING * 2,
    });
  }, [currentStep]);

  useEffect(() => {
    if (isOpen) setStepIndex(0);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isOpen) measureTarget();
  }, [isOpen, stepIndex, measureTarget]);

  useLayoutEffect(() => {
    if (isOpen) measureTarget();
  }, [isOpen, stepIndex, measureTarget]);

  useEffect(() => {
    if (!isOpen || !currentStep) return;

    const el = document.querySelector(`[data-tutorial="${currentStep.target}"]`);
    if (!el) return;

    const handleTransitionEnd = () => measureTarget();
    el.addEventListener("transitionend", handleTransitionEnd);

    const fallback = window.setTimeout(measureTarget, 250);

    return () => {
      el.removeEventListener("transitionend", handleTransitionEnd);
      window.clearTimeout(fallback);
    };
  }, [isOpen, stepIndex, currentStep, measureTarget]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("resize", measureTarget);
    window.addEventListener("scroll", measureTarget, true);
    return () => {
      window.removeEventListener("resize", measureTarget);
      window.removeEventListener("scroll", measureTarget, true);
    };
  }, [isOpen, measureTarget]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !currentStep) return;
    playLibrasWithLoading(currentStep);
  }, [isOpen, stepIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") goNext();
      if (event.key === "ArrowLeft") goBack();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, goNext, goBack]);

  if (!isOpen || !currentStep) return null;

  const placement = resolvePlacement(targetRect, currentStep.placement, isMobile);
  const tooltipStyle = getTooltipStyle(targetRect,
    placement,
    isMobile,
    currentStep.offsetX,
    currentStep.offsetY);

  return createPortal(
    <>
      <Overlay />
      <Spotlight
        style={{
          top: targetRect?.top ?? 0,
          left: targetRect?.left ?? 0,
          width: targetRect?.width ?? 0,
          height: targetRect?.height ?? 0,
        }}
      />
      <SpotlightOutline
        focus={Boolean(currentStep.target)}
        style={{
          top: targetRect?.top ?? 0,
          left: targetRect?.left ?? 0,
          width: targetRect?.width ?? 0,
          height: targetRect?.height ?? 0,
        }}
      />

      <TooltipBox style={tooltipStyle} $placement={placement} $mobile={isMobile} role="dialog" aria-modal="true">
        <h1>Lisa</h1>
        <CloseButton type="button" onClick={onClose} aria-label="Fechar tutorial">
          ×
        </CloseButton>

        <ContentRow>
          <TextContent>
            {currentStep.content.map((part, index) =>
              part.type === "text" ? (
                <span key={index}>{part.value}</span>
              ) : (
                <IconInline key={index} src={part.src} alt="" title={part.alt} aria-hidden="true" />
              )
            )}
          </TextContent>

          <LibrasButtonMenu
            disabled={loadingLibrasKey}
            onClick={() => playLibrasWithLoading(currentStep)}
          >
            {loadingLibrasKey ? (
              <span className="btnSpinner" />
            ) : (
              <img src={LibrasIcon} alt="Ícone Libras" />
            )}
          </LibrasButtonMenu>
        </ContentRow>
        <FooterRow>
          <ProgressContainer>
            <ProgressHeader>
              <span>Progresso</span>
              <span>
                {progress}%
              </span>
            </ProgressHeader>

            <ProgressBar>
              <ProgressFill
                $progress={progress}
              />
            </ProgressBar>
          </ProgressContainer>
          <span>
            <NavButtons>
              <NavButton type="button" onClick={goBack} disabled={isFirstStep} aria-label="Passo anterior" style={{ border: "none" }} $mobile={isMobile}>
                Voltar
              </NavButton>
              <NavButton type="button" onClick={goNext} className={isLastStep ? "finish" : ""} aria-label={isLastStep ? "Concluir tutorial" : "Próximo passo"} $mobile={isMobile}>
                {isLastStep ? "Concluir" : "Avançar"}
              </NavButton>
            </NavButtons>
          </span>
        </FooterRow>
      </TooltipBox>
    </>,
    document.body
  );
}
